import type { Theme } from "@fobu/schema";
import { onColor, rgba } from "@fobu/engine";

export { contrast, themeContrastWarnings } from "@fobu/engine";

const SYSTEM_SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
/** Schriftfamilien ohne Nachladen (DSGVO: keine Google-Fonts) – jeweils mit gängigen Systemschriften. */
export const FONT_STACKS: Record<string, string> = {
  System: SYSTEM_SANS,
  Serif: 'Charter, "Iowan Old Style", "Palatino Linotype", Georgia, Cambria, "Times New Roman", serif',
  Rounded: 'ui-rounded, "SF Pro Rounded", "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, "Arial Rounded MT", "Arial Rounded MT Bold", Calibri, source-sans-pro, sans-serif',
  Geometric: 'Avenir, Montserrat, Corbel, "URW Gothic", source-sans-pro, sans-serif',
  Humanist: 'Seravek, "Gill Sans Nova", Ubuntu, Calibri, "DejaVu Sans", source-sans-pro, sans-serif',
  Mono: 'ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, Consolas, "DejaVu Sans Mono", monospace',
  Slab: 'Rockwell, "Rockwell Nova", "Roboto Slab", "DejaVu Serif", "Sitka Small", serif',
};
const fontStack = (name: string) => FONT_STACKS[name] ?? (/^[A-Za-z0-9 \-]+$/.test(name) ? `"${name}", ${SYSTEM_SANS}` : SYSTEM_SANS);

/** Stil-Einstellungen als data-Attribute auf dem Wurzelelement (CSS reagiert darauf). */
export function themeAttrs(theme: Theme): Record<string, string> {
  return {
    "data-field": theme.fieldStyle,
    "data-choice": theme.choiceStyle,
    "data-button": theme.buttonStyle,
    "data-shape": theme.buttonShape,
    "data-density": theme.density,
    "data-shadow": theme.shadow,
    "data-card": theme.card,
    "data-layout": theme.layout,
    "data-progress": theme.progress,
    "data-motion": theme.motion,
    "data-logo": theme.logoAlign,
    "data-logo-size": theme.logoSize,
    ...(theme.glow ? { "data-glow": "" } : {}),
  };
}

/** Eigene Schrift aus der Bibliothek als @font-face. */
export function customFontCss(theme: Theme): string | null {
  const f = theme.customFont;
  if (!f || !/^[\w\-/.%:]+$/.test(f.url)) return null;
  return `@font-face{font-family:"FobuCustom";src:url("${f.url}");font-display:swap}`;
}

/** Theme → CSS-Variablen (werden auf das Wurzelelement des Formulars gesetzt). */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const c = theme.colors;
  const bg =
    theme.background.kind === "gradient"
      ? `linear-gradient(${theme.background.angle}deg, ${theme.background.from}, ${theme.background.to})`
      : theme.background.kind === "image"
        ? `linear-gradient(rgba(0,0,0,${theme.background.overlay}), rgba(0,0,0,${theme.background.overlay})), url("${encodeURI(theme.background.url)}") center / cover fixed`
        : c.background;
  return {
    "--fb-primary": c.primary,
    "--fb-on-primary": onColor(c.primary),
    "--fb-primary-soft": rgba(c.primary, 0.1),
    "--fb-primary-ring": rgba(c.primary, 0.45),
    "--fb-bg": c.background,
    "--fb-bg-paint": bg,
    "--fb-text": c.text,
    "--fb-muted": c.muted,
    "--fb-surface": c.surface,
    "--fb-line": rgba(c.text, 0.18),
    "--fb-line-strong": rgba(c.text, 0.42),
    "--fb-danger": c.danger,
    "--fb-danger-soft": rgba(c.danger, 0.08),
    "--fb-radius": `${theme.radius}px`,
    "--fb-radius-sm": `${Math.round(theme.radius * 0.6)}px`,
    "--fb-font-heading": theme.customFont && theme.customFont.useFor !== "body" ? `"FobuCustom", ${fontStack(theme.fontHeading)}` : fontStack(theme.fontHeading),
    "--fb-font-body": theme.customFont && theme.customFont.useFor !== "heading" ? `"FobuCustom", ${fontStack(theme.fontBody)}` : fontStack(theme.fontBody),
    "--fb-glow": rgba(c.primary, 0.55),
  };
}
